import { datadogRum } from '@datadog/browser-rum';
import { isAxiosError } from 'axios';
import { signInWithCredential, OAuthProvider } from 'firebase/auth';
import { safeAxios } from '@/services/safeAxios';
import { log } from '@/logger/logger.ts';
import { generateRandomString, sha256 } from '@/utils/auth.utils';
import { resumeTierCookie } from '@/services/axiosConfig';
import { API_ROUTES } from '@/api/apiRoutes';
import { posthog } from '@/providers/posthog';
import { FF_FIGMA_FILE_METADATA_SCOPE } from '@/consts/featureFlags';
import { getOAuthCallbackParams } from '@/utils/oauthCallbackParams';
import { runGuarded } from '@/services/waf/runGuarded';
import { auth } from '../../../firebase';
import { figma as figmaOidcConfig } from '../oidc-provider-configs';
import { INTERNAL_AUTH_ERROR_CODES, InternalAuthError } from '../firebase-errors';
import type { SignInResult } from '../../authProvider.types';

const logger = log.getLogger('figma-oidc-provider');
const PREFIX = 'weavy_figma_signin';
const FIGMA_OIDC_ACCESS_TOKEN_KEY = 'figma_access_token';
// ⚠️ DANGER: every scope in the requested string MUST also be in the Weave OAuth app's approved
// `allowed_scopes` (in EACH environment — staging + prod). Requesting a scope the app isn't allowlisted
// for makes the authorize endpoint reject the whole request with "Invalid scopes for app" (a sign-in /
// connect break). Get the scope added + approved on the OAuth app first.
//
// `file_content:read` powers FigmaNode connect/refresh; `file_create` creates a new drafts file when a
// FigmaNode is connected to a new draft.
const FIGMA_OAUTH_BASE_SCOPE = 'openid profile email file_content:read file_create';

// `file_metadata:read` reads the connected file's name (the `/files/:key/meta` route requires it) for the
// connected-state display. Gated behind a flag so it can be toggled / killed in prod without a deploy —
// defaults off, so we never request it (and never risk the invalid-scope break) unless the flag is on.
const getFigmaOauthScope = (): string =>
  posthog.isFeatureEnabled(FF_FIGMA_FILE_METADATA_SCOPE) === true
    ? `${FIGMA_OAUTH_BASE_SCOPE} file_metadata:read`
    : FIGMA_OAUTH_BASE_SCOPE;
/**
 * Fetches Figma ID token from server by exchanging authorization code
 */
interface FigmaTokenResponse {
  id_token: string;
}

type FigmaSignInStage = 'callback_validation' | 'token_exchange' | 'firebase_signin';
type FigmaTokenExchangeFailureReason =
  | 'request_rejected'
  | 'server_unavailable'
  | 'network_error'
  | 'unexpected_response'
  | 'response_invalid'
  | 'unexpected';

const recordFigmaOidcVital = (
  name: 'figma_oidc_token_exchange' | 'figma_oidc_firebase_signin' | 'figma_oidc_signin',
  startedAt: number,
  context: Record<string, string>,
): void => {
  const duration = performance.now() - startedAt;
  runGuarded(() =>
    datadogRum.addDurationVital(name, {
      startTime: Date.now() - duration,
      duration,
      context,
    }),
  );
};

const tokenExchangeFailureReason = (error: unknown): FigmaTokenExchangeFailureReason => {
  if (!isAxiosError(error)) return 'unexpected';
  if (!error.response) return 'network_error';
  if (error.response.status >= 400 && error.response.status < 500) return 'request_rejected';
  if (error.response.status >= 500) return 'server_unavailable';
  return 'unexpected_response';
};

const fetchFigmaToken = async (code: string): Promise<FigmaTokenResponse> => {
  const startedAt = performance.now();
  let responseInvalid = false;
  try {
    const redirectUri = `${window.location.origin}${window.location.pathname}`;
    // Session-establishing, like signin: skip the tier-cookie bootstrap prefetch (no session yet to mint
    // for), and pin retries off since retrying a lost-response exchange would re-send the single-use code.
    const response = await safeAxios.post<FigmaTokenResponse>(
      API_ROUTES.auth.figmaOidcToken(),
      { code, redirectUri },
      { withCredentials: true, skipTierBootstrap: true, 'axios-retry': { retries: 0 } },
    );
    if (!response.data || !response.data.id_token) {
      responseInvalid = true;
      throw new Error('No id_token received from server');
    }

    recordFigmaOidcVital('figma_oidc_token_exchange', startedAt, { outcome: 'success' });
    return response.data;
  } catch (error) {
    const reason = responseInvalid ? 'response_invalid' : tokenExchangeFailureReason(error);
    recordFigmaOidcVital('figma_oidc_token_exchange', startedAt, { outcome: 'error', reason });
    logger.error('Figma OIDC token exchange failed', { reason });
    throw error;
  }
};

/**
 * Initiates Figma OAuth redirect flow
 */
export const initiateFigmaRedirectSignIn = async (redirectPath: string): Promise<void> => {
  const redirectUri = encodeURIComponent(`${window.location.origin}${redirectPath}`);
  const clientId = figmaOidcConfig.clientId;
  const baseUrl = figmaOidcConfig.baseUrl;
  const state = generateRandomString(32);
  const rawNonce = generateRandomString(32);
  const hashedNonce = await sha256(rawNonce);

  // Store auth state
  localStorage.setItem(`${PREFIX}_nonce`, rawNonce);
  localStorage.setItem(`${PREFIX}_state`, state);

  // Store promotion query param if it exists, clear it if it doesn't
  const urlParams = new URLSearchParams(window.location.search);
  const promotion = urlParams.get('promotion');
  if (promotion) {
    localStorage.setItem(`${PREFIX}_sign_in_promotion`, promotion);
  } else {
    localStorage.removeItem(`${PREFIX}_sign_in_promotion`);
  }

  const fuid = urlParams.get('fuid');

  const scope = getFigmaOauthScope();
  const fuidParam = fuid ? `&fuid=${encodeURIComponent(fuid)}` : '';
  const oauthUrl = `${baseUrl}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}&nonce=${hashedNonce}${fuidParam}`;

  // Redirect the user to the authorization URL
  window.location.href = oauthUrl;
};

const CONNECT_PREFIX = 'weavy_figma_connect';

const buildFigmaOauthUrl = async (redirectUri: string): Promise<{ url: string; state: string }> => {
  const state = generateRandomString(32);
  const rawNonce = generateRandomString(32);
  const hashedNonce = await sha256(rawNonce);
  const scope = getFigmaOauthScope();
  const url =
    `${figmaOidcConfig.baseUrl}?response_type=code&client_id=${figmaOidcConfig.clientId}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${state}&nonce=${hashedNonce}`;
  return { url, state };
};

/**
 * Initiates Figma OAuth redirect for linking a Figma account (not sign-in).
 * Redirects directly to /settings, which is registered as a valid callback URL.
 */
export const initiateFigmaConnectRedirect = async (): Promise<void> => {
  const redirectUri = `${window.location.origin}/settings`;
  const { url, state } = await buildFigmaOauthUrl(redirectUri);
  localStorage.setItem(`${CONNECT_PREFIX}_state`, state);
  localStorage.setItem(`${CONNECT_PREFIX}_redirect_uri`, redirectUri);
  window.location.href = url;
};

/**
 * Opens a Figma OAuth flow for linking a Figma account in a centered popup.
 * Caller listens on the BroadcastChannel for the code and validates `state`.
 */
export const openFigmaConnectPopup = async (): Promise<{
  popup: Window;
  state: string;
  redirectUri: string;
}> => {
  const redirectUri = `${window.location.origin}/share`;
  const { url, state } = await buildFigmaOauthUrl(redirectUri);

  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  const features = `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=yes,status=no`;

  const popup = window.open(url, 'figma-oauth', features);
  if (!popup) {
    throw new Error('Popup blocked');
  }
  return { popup, state, redirectUri };
};

/**
 * Retrieves and clears the stored promotion parameter
 */
export const getStoredPromotion = (): string | null => {
  const promotion = localStorage.getItem(`${PREFIX}_sign_in_promotion`);
  localStorage.removeItem(`${PREFIX}_sign_in_promotion`);
  return promotion;
};

/**
 * Signs in with Figma using authorization code
 */
export const signInWithAuthorizationCode = async (authorizationCode: string): Promise<SignInResult> => {
  const startedAt = performance.now();
  let stage: FigmaSignInStage = 'callback_validation';
  try {
    if (!authorizationCode) {
      throw new InternalAuthError(INTERNAL_AUTH_ERROR_CODES.OIDC_MISSING_CODE, 'Figma authorization code is required');
    }

    const state = localStorage.getItem(`${PREFIX}_state`);
    const urlState = getOAuthCallbackParams().state;
    localStorage.removeItem(`${PREFIX}_state`);
    if (!state || !urlState) {
      throw new InternalAuthError(
        INTERNAL_AUTH_ERROR_CODES.OIDC_MISSING_STATE,
        'State is missing from the session storage or url',
      );
    }
    if (state !== urlState) {
      throw new InternalAuthError(INTERNAL_AUTH_ERROR_CODES.OIDC_STATE_MISMATCH, 'State parameter mismatch');
    }

    const nonce = localStorage.getItem(`${PREFIX}_nonce`);
    localStorage.removeItem(`${PREFIX}_nonce`);
    if (!nonce) {
      throw new InternalAuthError(
        INTERNAL_AUTH_ERROR_CODES.OIDC_MISSING_NONCE,
        'Nonce is missing from the session storage',
      );
    }

    /** A same-runtime callback must accept Core's HttpOnly exchange cookie after a logout. */
    resumeTierCookie();
    stage = 'token_exchange';
    const tokenResponse = await fetchFigmaToken(authorizationCode);
    // Create OAuth provider and credential, then sign in
    const figmaProvider = new OAuthProvider(figmaOidcConfig.oidcProvider);
    const credential = figmaProvider.credential({
      idToken: tokenResponse.id_token,
      rawNonce: nonce || '',
    });

    stage = 'firebase_signin';
    const firebaseStartedAt = performance.now();
    let result: SignInResult;
    try {
      result = await signInWithCredential(auth, credential);
      recordFigmaOidcVital('figma_oidc_firebase_signin', firebaseStartedAt, { outcome: 'success' });
    } catch (error) {
      recordFigmaOidcVital('figma_oidc_firebase_signin', firebaseStartedAt, { outcome: 'error' });
      logger.error('Figma Firebase credential sign-in failed');
      throw error;
    }

    recordFigmaOidcVital('figma_oidc_signin', startedAt, { outcome: 'success' });
    return result;
  } catch (error: unknown) {
    recordFigmaOidcVital('figma_oidc_signin', startedAt, { outcome: 'error', stage });
    logger.error('Figma OIDC sign-in failed', { stage });
    if (error instanceof InternalAuthError) throw error;
    throw new InternalAuthError(
      INTERNAL_AUTH_ERROR_CODES.OIDC_SIGN_IN_WITH_FIGMA_TOKEN_FAILED,
      'Error signing in with Figma token',
    );
  }
};

/**
 * Drain pre-migration tokens through their former maximum lifetime, even though no supported client
 * writes this key. Storage can be unavailable without preventing sign-in.
 */
export const clearOidcAccessToken = (): void => {
  try {
    localStorage.removeItem(FIGMA_OIDC_ACCESS_TOKEN_KEY);
  } catch {
    // Browser storage can be disabled; nothing reads this legacy key.
  }
};

clearOidcAccessToken();
